import { useBoolVariation } from '@launchdarkly/react-sdk';
import { RebrandedCompactLogo } from '@rsos/components/capstone/Layout/Navigation/common/logo/RebrandedLogos';
import VersionNumber from '@rsos/components/capstone/Layout/VersionNumber';
import styles from './RebrandedFooter.module.css';

// LaunchDarkly flag key; must match the dashboard.
const APP_VERSION_FOOTER_FLAG = 'authenticiation-version';

interface FooterLinkItem {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  items: FooterLinkItem[];
}

interface SocialLink {
  label: string;
  href: string;
  path: string;
}

// rapidsos.com marketing site, used to resolve the relative footer links.
const SITE = 'https://rapidsos.com';

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: 'Who We Serve',
    items: [
      {
        label: '911 (ECCs)',
        href: `${SITE}/public-safety/emergency-communications-centers/`,
      },
      {
        label: 'Fire / EMS',
        href: `${SITE}/public-safety/rapidsos-for-fire-ems/`,
      },
      {
        label: 'Law Enforcement',
        href: `${SITE}/public-safety/law-enforcement/`,
      },
      {
        label: 'State Agencies',
        href: `${SITE}/public-safety/state-911-government-solutions/`,
      },
      {
        label: 'Enterprise',
        href: `${SITE}/enterprise/enterprise-safety-solutions/`,
      },
    ],
  },
  {
    title: 'Products',
    items: [
      { label: 'Unite', href: `${SITE}/public-safety/unite/` },
      { label: 'Harmony AI', href: `${SITE}/public-safety/harmony/` },
      { label: 'IamResponding', href: 'https://iamresponding.com/' },
      { label: 'eDispatches', href: 'https://www.edispatches.com/' },
      { label: 'Northern911', href: 'https://northern911.com/' },
      { label: 'Total Response', href: 'https://totalresponse.com/' },
      { label: 'Create a Unite Account', href: 'https://rapidsosportal.com/' },
    ],
  },
  {
    title: 'Resources',
    items: [
      { label: 'Blog', href: `${SITE}/blog` },
      { label: 'Case Studies', href: `${SITE}/case-studies/` },
      { label: 'Customer Stories', href: `${SITE}/customer-stories/` },
      { label: 'Data Lab', href: `${SITE}/data-lab/` },
      { label: 'Developer Specs', href: 'https://developer.rapidsos.com/' },
      { label: 'Newsletter', href: `${SITE}/newsletter-signup/` },
      { label: 'Newsroom', href: `${SITE}/newsroom/` },
      { label: 'Videos', href: `${SITE}/videos/` },
      { label: 'Webinars', href: `${SITE}/webinars/` },
    ],
  },
  {
    title: 'Company',
    items: [
      { label: 'Careers', href: `${SITE}/careers/` },
      { label: 'Legal', href: `${SITE}/legal/` },
      { label: 'Licenses', href: `${SITE}/legal/licenses/` },
      { label: 'The Safety Network', href: `${SITE}/the-safety-network/` },
      { label: 'Security & Privacy', href: 'https://trust.rapidsos.com/' },
      { label: 'Terms', href: `${SITE}/legal/terms-of-use/` },
    ],
  },
];

const SOCIAL_LINKS: SocialLink[] = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/rapidsos',
    path: 'M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/teamrapidsos/',
    path: 'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.12 1.38C1.36 2.67.95 3.34.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.12.66.66 1.33 1.07 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.12-1.38.66-.66 1.07-1.33 1.38-2.12.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.12-.66-.66-1.33-1.07-2.12-1.38-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.41-10.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@Rapidsos',
    path: 'M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/rapidsos',
    path: 'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z',
  },
];

const RebrandedFooter = () => {
  // Show the app version (dropped in the rebrand) behind a LaunchDarkly flag.
  const showAppVersion = useBoolVariation(APP_VERSION_FOOTER_FLAG, false);

  return (
    <footer className={styles.footerWrapper}>
      <div className={styles.topSection}>
        <div className={styles.brandRow}>
          <RebrandedCompactLogo isDarkMode height={48} />
          <div className={styles.socialRow}>
            {SOCIAL_LINKS.map(({ label, href, path }) => (
              <a
                key={label}
                className={styles.socialLink}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={path} />
                </svg>
              </a>
            ))}
          </div>
        </div>
        <div className={styles.accentBar} />
      </div>

      <div className={styles.columns}>
        {FOOTER_COLUMNS.map(({ title, items }) => (
          <div key={title} className={styles.column}>
            <div className={styles.eyebrow}>{title}</div>
            <div className={styles.footerItems}>
              {items.map(({ label, href }) => (
                <a
                  key={label}
                  className={styles.footerItem}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.ruleBar} />
        <div className={styles.bottomRow}>
          <div className={styles.copyright}>
            © 2026 RapidSOS Inc. All rights reserved.
          </div>
          <button
            type="button"
            className={`${styles.privacyChoices} cky-banner-element`}
          >
            Your Privacy Choices
          </button>
          {showAppVersion && <VersionNumber color="background" size="normal" />}
        </div>
      </div>
    </footer>
  );
};

export default RebrandedFooter;
